/**
 * A forecast line's setup — doors, cases per store, growth, price, start month, and doors typed
 * for particular months — and the plan it generates.
 *
 * 1st Phorm (Bakhrom, 10/29):
 * - "Can't edit a line after creating it": a SKU row keeps its setup on `forecast_config`, so the
 *   Edit line dialog reads it back ([`line_setup_from_node`]) and rewrites the row's plan months from
 *   the edited setup ([`rebuild_line_plan`]) through the same generator a new source uses.
 * - "Allow changing the number of doors for a specific month": a door count typed for a month
 *   overrides what growth gives there ([`apply_door_overrides`]), the way a door edit on the grid
 *   does — on a per-store plan the case rate holds and revenue follows the doors; on an account
 *   total the cases follow the doors too.
 */
import type { TimePeriod } from "../types"
import { annual_growth_factor } from "./data_generator"
import { round_cases, type QuantityBasis } from "./quantity_basis"
import { MONTHS_PER_WEEK } from "./periods"

export interface LineSetup {
  initial_doors: number
  /** Cases per store per month. */
  cases_per_store_per_month: number
  /** Per year (0.02 = +2% doors after twelve months). */
  store_growth: number
  /** Per year. */
  same_store_growth: number
  cost_per_case: number
  start_period: string
  /** Door counts typed for particular months, overriding what growth gives there. */
  doors_by_period: Record<string, number>
}

interface PlanPointLike {
  period: { period_key: string }
  metrics: { store_count?: number; cases_sold?: number; units_sold?: number; volume_dollars?: number; case_cost?: number; [key: string]: unknown }
  is_actual?: boolean
  is_locked?: boolean
  source_kind?: string
  [key: string]: unknown
}

function months_per_period(period: TimePeriod | undefined): number {
  return period?.grain === "week" ? MONTHS_PER_WEEK : period?.grain === "quarter" ? 3 : 1
}

/**
 * The door count each period from the start gets: growth from the initial doors, unless a count
 * was typed for that period. Drives the "Doors by month" row the dialogs show.
 */
export function door_schedule(
  setup: Pick<LineSetup, "initial_doors" | "store_growth" | "start_period" | "doors_by_period">,
  periods: TimePeriod[],
): Array<{ period_key: string; doors: number; overridden: boolean }> {
  const start = periods.findIndex((period: TimePeriod) => period.period_key === setup.start_period)
  if (start === -1) return []
  return periods.slice(start).map((period: TimePeriod, index: number) => {
    const typed = setup.doors_by_period[period.period_key]
    if (typed != null && Number.isFinite(typed)) return { period_key: period.period_key, doors: typed, overridden: true }
    const grown = Math.round(setup.initial_doors * annual_growth_factor(setup.store_growth, index * months_per_period(period)))
    return { period_key: period.period_key, doors: grown, overridden: false }
  })
}

/** Apply typed door counts to generated plan points (see the module doc for what follows the doors). */
export function apply_door_overrides<T extends PlanPointLike>(
  points: T[],
  doors_by_period: Record<string, number> | undefined,
  quantity_basis: QuantityBasis | null | undefined,
): T[] {
  if (!doors_by_period || Object.keys(doors_by_period).length === 0) return points
  return points.map((point: T) => {
    const doors = doors_by_period[point.period.period_key]
    if (doors == null || !Number.isFinite(doors) || point.opportunity_base_metrics) return point
    const metrics = { ...point.metrics }
    const old_doors = Number(metrics.store_count ?? 0)
    const is_total = quantity_basis === "total"
    if (old_doors > 0) {
      const factor = doors / old_doors
      metrics.volume_dollars = Math.round(Number(metrics.volume_dollars ?? 0) * factor * 100) / 100
      if (is_total) {
        metrics.cases_sold = round_cases(Number(metrics.cases_sold ?? 0) * factor)
        if (metrics.units_sold != null) metrics.units_sold = Math.round(Number(metrics.units_sold) * factor)
      }
    } else if (!is_total) {
      // No doors before: a per-store rate times the new doors at the case price.
      metrics.volume_dollars = Math.round(doors * Number(metrics.cases_sold ?? 0) * Number(metrics.case_cost ?? 0) * 100) / 100
    }
    metrics.store_count = doors
    return { ...point, metrics }
  })
}

/** The setup a row was generated from, read back off the row (`forecast_config`, its plan months). */
export function line_setup_from_node(node: any, periods: TimePeriod[]): LineSetup {
  const config = node?.forecast_config ?? {}
  const rows: PlanPointLike[] = Array.isArray(node?.sales_data) ? node.sales_data : []
  const planned = rows
    .filter((row: PlanPointLike) => !row.is_actual && row.source_kind !== "actual" && Number(row.metrics?.store_count ?? 0) > 0)
    .map((row: PlanPointLike) => row.period.period_key)
    .sort()
  const start_period = planned[0] ?? periods[0]?.period_key ?? ""
  const setup: LineSetup = {
    initial_doors: Number(config.initial_doors ?? 0),
    cases_per_store_per_month: Number(config.initial_cases_per_month ?? 0),
    store_growth: Number(config.store_count_growth_multiplier ?? 0),
    same_store_growth: Number(config.same_store_sales_multiplier ?? 0),
    cost_per_case: Number(node?.case_cost ?? config.cost_per_case ?? 0),
    start_period,
    doors_by_period: {},
  }
  // A month whose doors differ from what the setup's growth gives was set by hand: keep it.
  const expected = new Map(door_schedule(setup, periods).map((entry) => [entry.period_key, entry.doors]))
  for (const row of rows) {
    if (row.is_actual || row.source_kind === "actual") continue
    const key = row.period.period_key
    const doors = Number(row.metrics?.store_count ?? 0)
    if (expected.has(key) && doors > 0 && doors !== expected.get(key)) setup.doors_by_period[key] = doors
  }
  return setup
}

/**
 * Replace a row's plan months with the ones `generated` (the generator's output for the edited
 * setup) carries. Actual and locked months stay as they are; months before the new start are
 * zeroed (the line starts later now); the setup is written back onto `forecast_config`.
 */
export function rebuild_line_plan(node: any, setup: LineSetup, generated: PlanPointLike[]): any {
  const by_period = new Map(generated.map((point: PlanPointLike) => [point.period.period_key, point]))
  const sales_data = (node.sales_data ?? []).map((row: PlanPointLike) => {
    if (row.is_actual || row.is_locked || row.source_kind === "actual") return row
    const next = by_period.get(row.period.period_key)
    if (next) {
      return { ...row, metrics: { ...row.metrics, ...next.metrics } }
    }
    if (row.period.period_key < setup.start_period) {
      return {
        ...row,
        metrics: { ...row.metrics, store_count: 0, cases_sold: 0, volume_dollars: 0, ...(row.metrics.units_sold != null ? { units_sold: 0 } : {}) },
      }
    }
    return row
  })
  const existing = new Set((node.sales_data ?? []).map((row: PlanPointLike) => row.period.period_key))
  for (const point of generated) {
    if (!existing.has(point.period.period_key)) sales_data.push(point)
  }
  return {
    ...node,
    case_cost: setup.cost_per_case,
    forecast_config: {
      ...(node.forecast_config ?? {}),
      initial_doors: setup.initial_doors,
      initial_cases_per_month: setup.cases_per_store_per_month,
      store_count_growth_multiplier: setup.store_growth,
      same_store_sales_multiplier: setup.same_store_growth,
      cost_per_case: setup.cost_per_case,
    },
    sales_data,
  }
}

/** The nodes from a root down to the node with `id`, or `[]` when it is not in the tree. */
export function node_path_by_id(nodes: any[], id: string): any[] {
  for (const node of nodes) {
    if (node?.id === id) return [node]
    const below = Array.isArray(node?.children) ? node_path_by_id(node.children, id) : []
    if (below.length > 0) return [node, ...below]
  }
  return []
}

/** The tree with the node `id` replaced by `replace(node)`; every other node is the same object. */
export function replace_node_by_id(nodes: any[], id: string, replace: (node: any) => any): any[] {
  let changed = false
  const next = nodes.map((node: any) => {
    if (node?.id === id) {
      changed = true
      return replace(node)
    }
    if (Array.isArray(node?.children) && node.children.length > 0) {
      const children = replace_node_by_id(node.children, id, replace)
      if (children !== node.children) {
        changed = true
        return { ...node, children }
      }
    }
    return node
  })
  return changed ? next : nodes
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("node_path_by_id / replace_node_by_id", () => {
    const tree = [{ id: "c", name: "Walmart @ Direct", level: "customer", children: [{ id: "s", name: "Sticks", level: "sku" }] }]
    it("finds the path to a leaf and replaces only it", () => {
      expect(node_path_by_id(tree, "s").map((n: any) => n.name)).toEqual(["Walmart @ Direct", "Sticks"])
      expect(node_path_by_id(tree, "nope")).toEqual([])
      const next = replace_node_by_id(tree, "s", (n: any) => ({ ...n, name: "Sticks 15ct" }))
      expect(next[0].children[0].name).toBe("Sticks 15ct")
      expect(tree[0].children[0].name).toBe("Sticks")
      expect(replace_node_by_id(tree, "nope", (n: any) => n)).toBe(tree)
    })
  })
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest
  const periods: TimePeriod[] = ["2027-01", "2027-02", "2027-03"].map((period_key: string) => ({
    period_key,
    year: 2027,
    month: Number(period_key.slice(5)),
  })) as TimePeriod[]

  function point(period_key: string, stores: number, cases: number, dollars: number, extra: Partial<PlanPointLike> = {}): PlanPointLike {
    return { period: { period_key }, metrics: { store_count: stores, cases_sold: cases, volume_dollars: dollars, case_cost: 10 }, ...extra }
  }

  describe("door_schedule", () => {
    it("grows doors per year from the start and shows a typed month as typed", () => {
      const schedule = door_schedule(
        { initial_doors: 100, store_growth: 0.12, start_period: "2027-02", doors_by_period: { "2027-03": 140 } },
        periods,
      )
      expect(schedule).toEqual([
        { period_key: "2027-02", doors: 100, overridden: false },
        { period_key: "2027-03", doors: 140, overridden: true },
      ])
    })
  })

  describe("apply_door_overrides", () => {
    it("holds a per-store case rate and moves revenue with the doors", () => {
      const out = apply_door_overrides([point("2027-01", 100, 2, 2000), point("2027-02", 100, 2, 2000)], { "2027-02": 150 }, "per_store")
      expect(out[0].metrics.store_count).toBe(100)
      expect(out[1].metrics).toMatchObject({ store_count: 150, cases_sold: 2, volume_dollars: 3000 })
    })

    it("moves an account total's cases with the doors too", () => {
      const out = apply_door_overrides([point("2027-01", 100, 200, 2000)], { "2027-01": 50 }, "total")
      expect(out[0].metrics).toMatchObject({ store_count: 50, cases_sold: 100, volume_dollars: 1000 })
    })

    it("prices a month that had no doors from the per-store rate", () => {
      const out = apply_door_overrides([point("2027-01", 0, 2, 0)], { "2027-01": 5 }, "per_store")
      expect(out[0].metrics).toMatchObject({ store_count: 5, volume_dollars: 100 })
    })
  })

  describe("line_setup_from_node / rebuild_line_plan", () => {
    const node = {
      case_cost: 10,
      forecast_config: { initial_doors: 100, initial_cases_per_month: 2, store_count_growth_multiplier: 0, same_store_sales_multiplier: 0.1 },
      sales_data: [
        point("2027-01", 0, 0, 0, { is_actual: true }),
        point("2027-02", 100, 2, 2000),
        point("2027-03", 120, 2, 2400),
      ],
    }

    it("reads the setup back, with a hand-set door month kept", () => {
      const setup = line_setup_from_node(node, periods)
      expect(setup).toMatchObject({ initial_doors: 100, cases_per_store_per_month: 2, same_store_growth: 0.1, cost_per_case: 10, start_period: "2027-02" })
      expect(setup.doors_by_period).toEqual({ "2027-03": 120 })
    })

    it("rewrites the plan months, keeps actuals, and writes the setup back", () => {
      const setup = { ...line_setup_from_node(node, periods), start_period: "2027-03", cost_per_case: 12 }
      const rebuilt = rebuild_line_plan(node, setup, [point("2027-03", 120, 3, 4320)])
      expect(rebuilt.sales_data[0]).toBe(node.sales_data[0])
      expect(rebuilt.sales_data[1].metrics).toMatchObject({ store_count: 0, cases_sold: 0, volume_dollars: 0 })
      expect(rebuilt.sales_data[2].metrics).toMatchObject({ store_count: 120, cases_sold: 3, volume_dollars: 4320 })
      expect(rebuilt.case_cost).toBe(12)
      expect(rebuilt.forecast_config.cost_per_case).toBe(12)
    })
  })
}
